import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { AuthError } from 'aws-amplify/auth';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';

import { Button } from '@shieldpay/bumblebee/button';
import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import { InputField } from '@shieldpay/bumblebee/input-field';
import { Text } from '@shieldpay/bumblebee/text';

const FieldSchema = z.object({
  mfaCode: z.string({
    required_error: 'login.validation.password.mfaSetupCode',
  }),
});

export type MfaFormFields = z.infer<typeof FieldSchema>;

export interface MfaFormProps {
  errorState: AuthError['name'] | undefined;
  resetErrorState: () => void;
  submitting?: boolean;
  onSubmit: (data: MfaFormFields) => void;
}

export const MfaForm = ({
  onSubmit,
  errorState,
  resetErrorState,
  submitting,
}: MfaFormProps) => {
  const { formatMessage } = useIntl();

  const [submittedFormData, setSubmittedFormData] = useState<FormData | null>();

  // lastResult should usually come from the server, but we need to handle it differently
  // here because Amplify Auth runs client-side
  const lastResult = useMemo(
    () =>
      submittedFormData
        ? parseWithZod(submittedFormData, {
            schema: FieldSchema.transform((data, ctx) => {
              if (errorState) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'login.validation.password.mfaSetupCode',
                  path: ['mfaCode'],
                });
                return z.NEVER;
              }

              return data;
            }),
          }).reply()
        : null,

    [errorState, submittedFormData],
  );

  const [form, fields] = useForm({
    lastResult,
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) => {
      if (errorState) resetErrorState();

      return parseWithZod(formData, { schema: FieldSchema });
    },

    onSubmit: (e, { formData, submission }) => {
      // Prevent the default form submission - auth is handled client-side
      e.preventDefault();

      // store the last submitted result so we can rerun validation with the external error state
      setSubmittedFormData(formData);

      onSubmit(FieldSchema.parse(submission?.payload));
    },
  });

  return (
    <Form
      {...getFormProps(form)}
      spacing="basePos3"
      title={formatMessage({ id: 'login.mfa.heading' })}
      autoComplete="off"
    >
      <Heading>
        <FormattedMessage id="login.mfa.heading" />
      </Heading>

      {fields.mfaCode.errors}

      <Text paragraph variant="caption">
        <FormattedMessage id="login.mfa.enterCode" />
      </Text>

      <InputField
        label={<FormattedMessage id="shared.enterCode" />}
        {...getInputProps(fields.mfaCode, { type: 'text' })}
        errors={fields.mfaCode.errors?.map((id) => formatMessage({ id }))}
      />

      <Button type="submit" size="large" loading={submitting}>
        <FormattedMessage id="shared.continue" />
      </Button>
    </Form>
  );
};
